<script setup>
/**
 * FloatingCartButton Component
 * Static floating cart button with bubble count (uniqueCount)
 * Position: fixed bottom-left (RTL)
 */
import { computed } from 'vue'
import { useRoute } from 'vue-router'
import { useCartStore } from '../../../app/store/cart.js'
import { ROUTES } from '../../../core/config/constants.js'

const emit = defineEmits(['open-cart'])

const route = useRoute()
const cartStore = useCartStore()

const bubbleCount = computed(() => cartStore.uniqueCount)
const showBubble = computed(() => bubbleCount.value > 0)

// Cart step of the desktop catalog guide (registered only on catalog routes)
const isCatalogRoute = computed(
  () => route.name === ROUTES.CATALOG || route.name === ROUTES.AGENTS_CATALOG
)
</script>

<template>
  <button
    v-tutorial="
      isCatalogRoute
        ? {
            id: 'catalog-desktop-cart',
            text: 'כל המוצרים שהוספתם מחכים כאן — לחצו לצפייה בעגלה ולסיום ההזמנה',
            importance: 'important',
            showOnce: true,
            autoTrigger: false,
            group: 'catalog-desktop-intro',
            step: 5,
          }
        : null
    "
    class="floating-cart-btn"
    aria-label="פתח עגלת קניות"
    @click="$emit('open-cart')"
  >
    <!-- Cart Icon -->
    <svg
      class="cart-icon"
      viewBox="0 0 24 24"
      width="24"
      height="24"
      fill="none"
      stroke="currentColor"
      stroke-width="2"
    >
      <circle cx="9" cy="21" r="1" />
      <circle cx="20" cy="21" r="1" />
      <path d="M1 1h4l2.68 13.39a2 2 0 002 1.61h9.72a2 2 0 002-1.61L23 6H6" />
    </svg>

    <!-- Bubble Count -->
    <Transition name="bubble">
      <span v-if="showBubble" class="cart-bubble">
        {{ bubbleCount > 99 ? '99+' : bubbleCount }}
      </span>
    </Transition>
  </button>
</template>

<style scoped>
.floating-cart-btn {
  position: fixed;
  bottom: 24px;
  left: 24px;
  z-index: 1000;

  display: flex;
  align-items: center;
  justify-content: center;

  width: 56px;
  height: 56px;

  background: var(--gradient-header);
  border: none;
  border-radius: var(--border-radius-full);

  color: white;
  cursor: pointer;

  box-shadow:
    0 4px 12px rgba(30, 41, 59, 0.3),
    0 2px 4px rgba(0, 0, 0, 0.1);

  transition: all var(--transition-fast);
}

.floating-cart-btn:hover {
  transform: translateY(-2px);
  box-shadow:
    0 6px 20px rgba(30, 41, 59, 0.4),
    0 4px 8px rgba(0, 0, 0, 0.15);
}

.floating-cart-btn:active {
  transform: translateY(0);
}

.floating-cart-btn:focus {
  outline: none;
}

.cart-icon {
  flex-shrink: 0;
}

/* Bubble */
.cart-bubble {
  position: absolute;
  top: -4px;
  right: -4px;

  min-width: 20px;
  height: 20px;
  padding: 0 6px;

  display: flex;
  align-items: center;
  justify-content: center;

  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  border: 2px solid white;
  border-radius: var(--border-radius-full);

  font-size: 11px;
  font-weight: var(--font-weight-bold);
  color: white;

  box-shadow: 0 2px 8px rgba(239, 68, 68, 0.4);
}

/* Bubble Animation */
.bubble-enter-active {
  animation: bubblePop 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.bubble-leave-active {
  animation: bubblePop 0.2s ease-out reverse;
}

@keyframes bubblePop {
  0% {
    opacity: 0;
    transform: scale(0);
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
}
</style>
